import { setLocale } from '@/i18n';
import { useSettingsStore } from '@/stores/settingsStore';
import { createRouter, createWebHistory } from 'vue-router';
import { authService } from '../services/authService';

const DEFAULT_LANG = 'de';

function getPreferredLanguage(): string {
  return DEFAULT_LANG; // Immer 'de' zurückgeben
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // Root-Route mit intelligenter Sprachweiterleitung
    {
      path: '/',
      redirect: () => `/${getPreferredLanguage()}`,
      meta: { requiresAuth: false, isPublic: true }
    },
    // Login-Route mit Sprachweiterleitung
    {
      path: '/login',
      redirect: () => `/${getPreferredLanguage()}/login`,
      meta: { requiresAuth: false, isPublic: true }
    },
    {
      path: '/register',
      redirect: (to) => ({
        name: 'register-lang',
        params: { lang: getPreferredLanguage() },
        query: to.query
      }),
      meta: { requiresAuth: false, isPublic: true }
    },
    {
      path: '/sso',
      name: 'sso',
      component: () => import('../views/Sso.vue'),
      meta: { requiresAuth: false, isPublic: true }
    },
    // Öffentliche THG-Einsparungsanzeige für die Tankstelle
    {
      path: '/thg-anzeige',
      name: 'thg-anzeige',
      component: () => import('../views/ThgEinsparungView.vue'),
      meta: { requiresAuth: false, isPublic: true }
    },
    {
      path: '/:lang',
      children: [
        // Login / Home
        {
          path: 'login',
          name: 'login-lang',
          component: () => import('../views/LoginView.vue'),
          meta: { requiresAuth: false, isPublic: true }
        },
        {
          path: 'register',
          name: 'register-lang',
          component: () => import('../views/RegisterView.vue'),
          meta: { requiresAuth: false, isPublic: true }
        },
        {
          path: '',
          name: 'home-lang',
          component: () => import('../views/HomeView.vue'),
          meta: { requiresAuth: false, isPublic: true }
        },
        {
          path: 'sso',
          name: 'sso-lang',
          component: () => import('../views/Sso.vue'),
          meta: { requiresAuth: false, isPublic: true }
        },
        // THG-Einsparungsanzeige auch mit Sprachpräfix verfügbar
        {
          path: 'thg-anzeige',
          name: 'thg-anzeige-lang',
          component: () => import('../views/ThgEinsparungView.vue'),
          meta: { requiresAuth: false, isPublic: true }
        },

        // Bewegungsdaten
        {
          path: 'bewegungsdaten',
          children: [
            {
              path: 'einlieferungen',
              name: 'einlieferungen',
              component: () => import('../views/bewegungsdaten/EinlieferungenView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'einlieferungen/:id',
              name: 'einlieferungen-edit',
              component: () => import('../views/bewegungsdaten/EinlieferungenEditView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'ablieferungen',
              name: 'ablieferungen',
              component: () => import('../views/bewegungsdaten/AblieferungenView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'ablieferungen/:id',
              name: 'ablieferungen-edit',
              component: () => import('../views/bewegungsdaten/AblieferungenEditView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'abholungen',
              name: 'abholungen',
              component: () => import('../views/bewegungsdaten/AbholungView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'abholungen/:id',
              name: 'abholungen-edit',
              component: () => import('../views/bewegungsdaten/AbholungEditView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'bestaende',
              name: 'bestaende',
              component: () => import('../views/bewegungsdaten/BestaendeView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'bestaende/create',
              name: 'bestaende-create',
              component: () => import('../views/bewegungsdaten/BestaendeCreateView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'bestaende/:id',
              name: 'bestaende-edit',
              component: () => import('../views/bewegungsdaten/BestaendeEditView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'nachweisbescheinigungen',
              name: 'nachweisbescheinigungen',
              component: () => import('../views/bewegungsdaten/NachweisbescheinigungView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'nachweisbescheinigungen/:id',
              name: 'nachweisbescheinigungen-detail',
              component: () =>
                import('../views/bewegungsdaten/NachweisbescheinigungDetailView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'tankstelle-bestand',
              name: 'tankstelle-bestand',
              component: () => import('../views/bewegungsdaten/TankstelleBestandView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'nachweisverfolgung',
              name: 'nachweisverfolgung',
              component: () => import('../views/bewegungsdaten/NachweisverfolgungView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'nachweisverfolgung/:id',
              name: 'nachweisverfolgung-detail',
              component: () => import('../views/bewegungsdaten/NachweisverfolgungDetailView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'nachweisverteilung',
              name: 'nachweisverteilung',
              component: () => import('../views/bewegungsdaten/NachweisverteilungView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'nachweise',
              name: 'nachweise',
              component: () => import('../views/bewegungsdaten/ZertifikateView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'nachweise/:id',
              name: 'nachweise-edit',
              component: () => import('../views/bewegungsdaten/ZertifikatEditView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'bilanzen',
              name: 'bilanzen',
              component: () => import('../views/bewegungsdaten/BilanzenView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'bilanzen/:id',
              name: 'bilanzen-edit',
              component: () => import('../views/bewegungsdaten/BilanzEditView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'pocs',
              name: 'pocs',
              component: () => import('../views/bewegungsdaten/PocsView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'import',
              name: 'bewegungsdaten-import',
              component: () => import('../views/bewegungsdaten/Import.vue'),
              meta: { requiresAuth: true }
            }
          ]
        },

        // Stammdaten
        {
          path: 'stammdaten',
          children: [
            {
              path: 'allgemein',
              name: 'allgemein',
              component: () => import('../views/stammdaten/GeneralView.vue'),
              meta: { requiresAuth: true, requiresAdmin: true }
            },
            {
              path: 'tauschpartner',
              name: 'tauschpartner',
              component: () => import('../views/stammdaten/PartnerView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'tauschpartner/:id',
              name: 'tauschpartner-edit',
              component: () => import('../views/stammdaten/PartnerEditView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'standorte',
              name: 'standorte',
              component: () => import('../views/stammdaten/StandorteView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'standorte/:id',
              name: 'standorte-edit',
              component: () => import('../views/stammdaten/StandortEditView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'produkte',
              name: 'produkte',
              component: () => import('../views/stammdaten/ProdukteView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'produkte/:id',
              name: 'produkte-edit',
              component: () => import('../views/stammdaten/ProduktEditView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'zertifizierungen',
              name: 'zertifizierungen',
              component: () => import('../views/stammdaten/ZertifizierungenView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'import',
              name: 'stammdaten-import',
              component: () => import('../views/stammdaten/Import.vue'),
              meta: { requiresAuth: true }
            }
          ]
        },

        // Reports
        {
          path: 'reports',
          children: [
            {
              path: 'liste',
              name: 'reports-liste',
              component: () => import('../views/reports/ReportsListeView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'ytd',
              name: 'reports-ytd',
              component: () => import('../views/reports/YTDView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'auswertungen',
              name: 'auswertungen',
              component: () => import('../views/reports/AuswertungenView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'logmeldungen',
              name: 'logmeldungen',
              component: () => import('../views/reports/LogmeldungenView.vue'),
              meta: { requiresAuth: true, requiresSupport: true }
            }
          ]
        },
        {
          path: 'reports/logmeldungen',
          component: () => import('../views/reports/LogmeldungenView.vue')
        },

        {
          path: 'import',
          component: () => import('../views/import/Import.vue'),
          meta: { requiresAuth: true }
        },

        // Hilfe
        {
          path: 'hilfe',
          children: [
            {
              path: 'dokumentation',
              name: 'dokumentation',
              component: () => import('../views/hilfe/DokumentationView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'faq',
              name: 'faq',
              component: () => import('../views/hilfe/FAQView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'hilfevideos',
              name: 'hilfevideos',
              component: () => import('../views/hilfe/HilfevideosView.vue'),
              meta: { requiresAuth: true }
            }
          ]
        },

        // Benutzer
        {
          path: 'benutzer',
          children: [
            {
              path: 'dashboard',
              name: 'dashboard',
              component: () => import('../views/benutzer/DashboardView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'passwort',
              name: 'passwort-wechseln',
              component: () => import('../views/benutzer/PasswortWechselnView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'einstellungen',
              name: 'einstellungen',
              component: () => import('../views/benutzer/EinstellungenView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'berechtigungen',
              name: 'berechtigungen',
              component: () => import('../views/benutzer/BerechtigungenView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'berechtigungsverwaltung',
              name: 'berechtigungsverwaltung',
              component: () => import('../views/benutzer/BerechtigungsVerwaltungView.vue'),
              meta: { requiresAuth: true, requiresAdmin: true }
            },
            {
              path: 'nachrichten',
              name: 'nachrichten',
              component: () => import('../views/benutzer/NachrichtenView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'administration',
              name: 'benutzer-administration',
              component: () => import('../views/benutzer/BenutzerAdministrationView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'mandanten',
              name: 'mandanten-verwaltung',
              component: () => import('../views/benutzer/MandantenView.vue'),
              meta: { requiresAuth: true, requiresAdmin: true }
            },
            {
              path: 'rollen',
              name: 'rollen-verwaltung',
              component: () => import('../views/benutzer/RollenView.vue'),
              meta: { requiresAuth: true, requiresAdmin: true }
            },
            {
              path: 'release-notes',
              name: 'release-notes',
              component: () => import('../views/benutzer/ReleaseNotesView.vue'),
              meta: { requiresAuth: true }
            }
          ]
        },

        // Layouts (angepasste Pfade, da AboutView & Co. im root von views liegen)
        {
          path: 'layouts/about',
          component: () => import('../views/AboutView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/accordion',
          component: () => import('../views/AccordionView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/badge',
          component: () => import('../views/BadgeView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/breadcrumb',
          component: () => import('../views/BreadcrumbView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/button-group',
          component: () => import('../views/ButtonGroupView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/button-icons',
          component: () => import('../views/ButtonIconsView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/button',
          component: () => import('../views/ButtonView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/data-display',
          component: () => import('../views/DataDisplayView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/date-picker',
          component: () => import('../views/DatePickerView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/input',
          component: () => import('../views/InputView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/pagination',
          component: () => import('../views/PaginationView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/sidebar',
          component: () => import('../views/SidebarView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/table',
          component: () => import('../views/TableView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/tabs-group',
          component: () => import('../views/TabsGroupView.vue'),
          meta: { requiresAuth: true }
        },

        // 404-Route für nicht gefundene Seiten innerhalb der Sprach-Route
        {
          path: ':pathMatch(.*)*',
          name: 'not-found-lang',
          component: () => import('../views/NotFoundView.vue'),
          meta: { requiresAuth: false, isPublic: true }
        }
      ]
    },
    // Globale 404-Route
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundView.vue'),
      meta: { requiresAuth: false, isPublic: true }
    }
  ]
});

// Navigation Guard
router.beforeEach(async (to, from, next) => {
  // Sofortige Prüfung auf Redirect-Flag
  const redirectedToLogin = sessionStorage.getItem('redirected_to_login') === 'true';

  // Wenn bereits weitergeleitet und es handelt sich nicht um einen Login-Versuch, bleibe auf Login
  if (redirectedToLogin && to.name !== 'login-lang' && !to.path.includes('/login')) {
    console.log('Navigation blockiert wegen vorherigem Redirect', to.fullPath);
    next({ name: 'login-lang', params: { lang: 'de' } });
    return;
  }

  // Ignoriere Navigation, wenn es sich um einen Redirect zur Login-Seite handelt und wir bereits dort unterwegs sind
  if (from.name === 'login-lang' && to.name === 'login-lang') {
    console.log('Verhindere Login-Redirect-Schleife');
    next();
    return;
  }

  const isAuthenticated = await authService.isAuthenticated();
  const settingsStore = useSettingsStore();

  // Prüfe ob die Session noch gültig ist
  if (isAuthenticated) {
    // Bei erfolgreicher Authentifizierung das Redirect-Flag zurücksetzen
    sessionStorage.removeItem('redirected_to_login');

    const remainingTime = authService.getRemainingTime();
    console.log(
      `Router Navigation Guard: Verbleibende Zeit: ${remainingTime / 1000}s, Route: ${to.fullPath}`
    );
    if (remainingTime <= 0) {
      console.log(
        `Router Navigation Guard: Session abgelaufen - Logout ausgelöst, verbleibende Zeit: ${remainingTime / 1000}s`
      );
      await authService.logout();
      // Verhindern, dass wir zur Login-Seite weiterleiten, wenn wir bereits dort sind
      if (to.name !== 'login-lang') {
        // Setze das Redirect-Flag, damit keine weiteren Redirects passieren
        sessionStorage.setItem('redirected_to_login', 'true');
        next({
          name: 'login-lang',
          query: { redirect: to.fullPath },
          params: { lang: to.params.lang || DEFAULT_LANG }
        });
      } else {
        next();
      }
      return;
    }
  }

  // Handle 404 errors
  if (to.name === 'not-found' || to.name === 'not-found-lang') {
    next();
    return;
  }

  // Check language in URL and set it
  const urlLang = to.params.lang as string;
  const path = to.path;
  const query = to.query;

  if (urlLang) {
    // Nur 'de' und 'en' sind erlaubt
    if (urlLang !== 'de' && urlLang !== 'en') {
      next('/de' + path.substring(3)); // Ersetze ungültige Sprache durch 'de'
      return;
    }

    // Setze die Sprache, wenn sie sich geändert hat
    if (urlLang !== settingsStore.settings.language) {
      await settingsStore.saveSettings({
        ...settingsStore.settings,
        language: urlLang
      });
      setLocale(urlLang);
      sessionStorage.setItem('locale', urlLang);
    }
  } else if (!path.startsWith('/de/') && !path.startsWith('/en/')) {
    // Wenn keine Sprache in der URL ist, leite zu /de/ weiter
    const newPath = path === '/' ? '/de' : '/de' + path;
    if (query) {
      next({
        path: newPath,
        query: { ...query }
      });
    } else {
      next(newPath);
    }
    return;
  }

  // Verhindere Zugriff auf Login-Seite wenn bereits eingeloggt
  if (isAuthenticated && to.name === 'login-lang') {
    next({ name: 'dashboard', params: { lang: urlLang || DEFAULT_LANG } });
    return;
  }

  // Erlaube Zugriff auf öffentliche Routen ohne weitere Prüfungen
  if (to.meta.isPublic) {
    next();
    return;
  }

  // Prüfe Berechtigungen für geschützte Routen
  if (to.meta.requiresAuth) {
    // Prüfe, ob der Benutzer nicht authentifiziert ist
    if (!isAuthenticated) {
      // Wenn wir bereits zum Login weitergeleitet wurden und nun zu einer geschützten Route navigieren,
      // verhindern wir eine weitere Weiterleitung zum Login
      if (redirectedToLogin) {
        console.log(
          'Navigation zu geschützter Route ohne Authentifizierung verhindert, bereits am Login'
        );
        // Wenn wir bereits auf Login-Seite sind, einfach dort bleiben
        if (to.name === 'login-lang') {
          next();
        } else {
          // Ansonsten zur Login-Seite weiterleiten (ohne erneutes Flag zu setzen)
          next({
            name: 'login-lang',
            params: { lang: urlLang || DEFAULT_LANG }
          });
        }
        return;
      }

      // Erste Weiterleitung zum Login - setze Flag
      console.log('Erste Weiterleitung zum Login - setze Flag');
      sessionStorage.setItem('redirected_to_login', 'true');

      // Speichere aktuelle URL für Redirect nach Login
      const redirectPath = to.fullPath;

      // Vermeide doppelte Redirects zum Login
      if (!path.includes('/login')) {
        next({
          name: 'login-lang',
          query: { redirect: redirectPath },
          params: { lang: urlLang || DEFAULT_LANG }
        });
        return;
      }
    }

    if (isAuthenticated) {
      // Lade Benutzerberechtigungen dynamisch
      const { permissionService } = await import('../services/permissionService');
      await permissionService.initAsync();

      // Prüfe Admin-Berechtigung
      if (to.meta.requiresAdmin && !authService.isAdmin()) {
        next({ name: 'dashboard', params: { lang: urlLang || DEFAULT_LANG } });
        return;
      }

      // Prüfe Route-spezifische Berechtigungen
      if (to.name && typeof to.name === 'string') {
        if (!permissionService.canAccessRoute(to.name)) {
          next({
            name: 'dashboard',
            params: { lang: urlLang || DEFAULT_LANG }
          });
          return;
        }
      }

      // Prüfe Mandanten-Zuordnung
      const currentMandant = authService.getMandant();
      if (!currentMandant) {
        await authService.logout();
        next({
          name: 'login',
          params: { lang: urlLang || DEFAULT_LANG }
        });
        return;
      }
    }
  }

  next();
});

export default router;
